/**
 * TOSS Web Application — the side navigation, one frame for both audiences
 *
 * Fourth shared layer of the product, after the chat engine, the application card and the top
 * bar — and the one the owner asked for on 2026-08-26 looking at the two rails side by side:
 * «в клиенте размер иконок и текста лучше, и то, как выбран пункт выглядит».
 *
 * They had drifted the way two copies do: 52 px rows against 40, a 24 px glyph against 18, and
 * an active row that was `#FAFAFA` on one side — a white so close to the background that the
 * lit item was findable only by elimination — against a tinted `primary-50` on the other. Five
 * of the internal rail's colours were hex literals outside the token set.
 *
 * Same rule as the rest of these layers (see `vault/knowledge/patterns/two audiences share a
 * rendered node, never a DTO.md`): this one holds the frame and the row, and knows nothing about
 * who is looking. No `surface` prop, no store, no query, no permission key — ESLint pins that.
 * Both rails fill the same four slots and keep what is theirs:
 *
 *  - internal — the ⌘K search row, permission filtering, renamed labels from the i18n store;
 *  - portal — «Новая заявка» above the menu, unread badges, the customer's capability filter.
 *
 * Width is 248 on both since 2026-08-26 (owner). The internal rail was 280 (`w-sidebar`); the
 * portal's had already come down from 308 for the same reason — the menu does not need it and
 * the content does.
 */

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface SidebarShellProps extends HTMLAttributes<HTMLElement> {
  /** Logo, product name and the collapse toggle — see `SidebarBrand`. */
  brand?: ReactNode;
  /** Whatever sits between the brand and the menu: a search row, a primary action. */
  top?: ReactNode;
  /** The menu itself. */
  children?: ReactNode;
  /** The account block at the foot. */
  footer?: ReactNode;
  /** Icon-only mode: always on the tablet, opt-in on the computer. */
  collapsed?: boolean;
  /** Accessible name of the `<nav>` — the two menus are not the same menu. */
  navLabel?: string;
  /**
   * Как рейл стоит на странице.
   *
   * `card` (по умолчанию) — карточка на канве: отступ от краёв, радиус, рамка по кругу. Так он
   * выглядит на компьютере и планшете, где вокруг него есть поле.
   *
   * `flush` — вплотную к краям, рамка только справа. Единственный его потребитель — выезжающий
   * ящик админки на телефоне: там рейл занимает край экрана целиком, и скруглять с отступом
   * нечего — карточка внутри выехавшей панели читалась бы как вторая рамка.
   */
  variant?: 'card' | 'flush';
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export const SidebarShell = forwardRef<HTMLElement, SidebarShellProps>(
  (
    {
      brand,
      top,
      children,
      footer,
      collapsed = false,
      navLabel = 'Основное меню',
      variant = 'card',
      className = '',
      ...props
    },
    ref
  ) => {
    const isCard = variant === 'card';

    const classes = [
      'flex flex-col',
      // Карточка тянется до высоты строки flex-контейнера САМА (`align-items: stretch`), и
      // `h-full` ей противопоказан: со внешним отступом высота стала бы 100 % + два зазора, и
      // низ уехал бы под край окна. Ящику на телефоне, наоборот, тянуться не от чего — его
      // родитель `fixed inset-y-0`, а не flex.
      isCard ? 'app-surface my-[var(--sidebar-gap)] ml-[var(--sidebar-gap)]' : 'h-full',
      // GPU-accelerated width transition, defined in index.css. It also puts `contain: layout
      // style` on this element, which makes it a containing block — the reason the account menu
      // below opens through a portal rather than with `absolute`.
      'sidebar-transition',
      'relative',
      collapsed ? 'w-16' : 'w-[248px]',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <aside
        ref={ref}
        className={classes}
        // Поверхность рейла — карточка (решение владельца 27.08.2026, после того как тёмный
        // брендовый рейл посмотрели вживую): «боковое меню белым, как карточка, и по краям
        // закруглить». Роли на экране разводит не цвет стенки, а форма: слева объект на канве,
        // справа объекты на канве, между ними серое поле.
        //
        // Вид карточки берётся классом `app-surface`, а не инлайн-стилем: до 03.09.2026 рейл был
        // единственной поверхностью продукта, которую нельзя было переодеть ни одним правилом —
        // инлайн выигрывает у любого CSS. Ящик на телефоне (`flush`) остаётся стеной со своей
        // правой линией, и она пишется здесь.
        style={
          isCard
            ? undefined
            : {
                backgroundColor: 'var(--sidebar-bg)',
                borderRight: '1px solid var(--sidebar-border)',
              }
        }
        {...props}
      >
        {brand}

        {top}

        {/* The scrollbar is hidden rather than styled: on a rail this narrow it eats a third of
            the space a label has, and the menu is short enough to reach by dragging. */}
        <nav
          aria-label={navLabel}
          className="flex-1 px-2 pb-4 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {children}
        </nav>

        {footer}
      </aside>
    );
  }
);

SidebarShell.displayName = 'SidebarShell';

export default SidebarShell;
